/**
 * Session titles in the console: the mission's title with a Mission chip for a session Orgus
 * started for a mission (shownTitle.ts), the first prompt for every other one.
 */
import { useCallback, useMemo } from 'react';
import type { Id, Session } from '@orgus/protocol';
import { cn } from '@orgus/ui';
import { useOrgus } from '../../lib/store.ts';
import { missionsSlice } from '../missions/model.ts';
import { feedTitle, shownTitle } from './shownTitle.ts';
import type { ShownTitle } from './shownTitle.ts';

export { missionIdOfTitle, shownTitle } from './shownTitle.ts';
export type { ShownTitle } from './shownTitle.ts';

/** shownTitle bound to the missions the hub knows. */
export function useSessionTitle(): (s: Pick<Session, 'title'>) => ShownTitle {
  const missions = useOrgus((st) => missionsSlice(st.ext)?.missions);
  const byId = useMemo(() => new Map((missions ?? []).map((m) => [m.id, m.title])), [missions]);
  return useCallback((s) => shownTitle(s, (id) => byId.get(id)), [byId]);
}

/** feedTitle bound to the missions the hub knows: an event names a mission by its title. */
export function useFeedTitle(): (title: string) => string {
  const missions = useOrgus((st) => missionsSlice(st.ext)?.missions);
  const byId = useMemo(() => new Map((missions ?? []).map((m) => [m.id, m.title])), [missions]);
  return useCallback((t) => feedTitle(t, (id) => byId.get(id)), [byId]);
}

/**
 * The model a mission asked for when it started this session: a session that failed before its
 * first answer never reports one, so its drawer says what it was started with, not "Default".
 */
export function useLaunchModel(sessionId: Id | null | undefined): string | null {
  return useOrgus((st) => {
    if (!sessionId) return null;
    for (const m of missionsSlice(st.ext)?.missions ?? []) {
      const l = m.launches.find((x) => x.sessionId === sessionId);
      if (l) return l.model ?? m.model ?? null;
    }
    return null;
  });
}

/** The title on one line, with a Mission chip when a mission started the session. */
export function SessionTitle({ session, className, chip = true }: { session: Pick<Session, 'title'>; className?: string; chip?: boolean }) {
  const t = useSessionTitle()(session);
  return (
    <span className="flex min-w-0 items-center gap-2">
      <span className={cn('min-w-0 truncate', className)} title={t.title}>
        {t.title}
      </span>
      {chip && t.missionId && <MissionChip />}
    </span>
  );
}

/** The one chip a session row may carry when a mission started it. */
export function MissionChip({ className }: { className?: string }) {
  return (
    <span data-chip className={cn('inline-flex h-5 shrink-0 items-center rounded-control bg-hover px-2 text-caption text-fg-muted', className)}>
      Mission
    </span>
  );
}
